/*
 * ============================================================================
 * LOADER REDESIGN — 2026-05-26
 * ============================================================================
 * Modern light-mode-first loader system. Single conic-gradient arc primitive
 * used at multiple sizes, plus a centered chassis circle (matches the 44px
 * empty-state icon pattern from SitesPage / AlertsPage / AdminPage).
 *
 * Tokens hardcoded to light-mode values where CSS vars may not be loaded yet
 * (e.g. BootScreen runs before ThemeProvider effects fire); everything else
 * uses CSS vars so dark mode still works.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const ACCENT_BLUE = '#1a5dc8';
const BG_PRIMARY = '#fafbfe';
const BORDER_SUBTLE = '#e4e8ee';

const GlobalKeyframes = () => (
  <style jsx global>{`
    @keyframes si-arc-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes si-boot-pulse {
      0%, 100% { opacity: 0.55; }
      50% { opacity: 1; }
    }
    @keyframes si-boot-slide {
      0%   { transform: translateX(-24px); opacity: 0; }
      18%  { opacity: 1; }
      82%  { opacity: 1; }
      100% { transform: translateX(96px); opacity: 0; }
    }
    @keyframes si-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `}</style>
);

/**
 * Internal primitive — conic-gradient ring with smooth tail fade.
 * Used everywhere a spinner is shown.
 */
const Arc = ({ size = 32, thickness, color = 'var(--accent-blue)', opacity = 1, style = {} }) => {
  const t = thickness ?? (size <= 14 ? 1.5 : size <= 22 ? 2 : size <= 36 ? 2.5 : size <= 52 ? 3 : 3.5);
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(from 0deg, transparent 0deg, ${color} 280deg, transparent 360deg)`,
        WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${t + 0.5}px), #000 calc(100% - ${t}px))`,
        mask: `radial-gradient(farthest-side, transparent calc(100% - ${t + 0.5}px), #000 calc(100% - ${t}px))`,
        animation: 'si-arc-spin 900ms cubic-bezier(0.65, 0, 0.35, 1) infinite',
        opacity,
        ...style,
      }}
    />
  );
};

/**
 * Delay-before-show hook — prevents flashing the loader on sub-threshold loads.
 */
const useDelayedShow = (delay = 250) => {
  const [show, setShow] = useState(delay === 0);
  useEffect(() => {
    if (delay === 0) return;
    const id = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(id);
  }, [delay]);
  return show;
};

/**
 * Loader — default export. Standalone conic arc on the page background
 * (no chassis circle — cleaner light-mode read).
 * Sizes:
 *   small  — 20px arc (compact, inline-ish)
 *   medium — 32px arc (card / section)
 *   large  — 44px arc (full-page route loaders)
 *   xlarge — 56px arc (hero / boot fallback)
 */
const Loader = ({
  size = 'medium',
  text = 'Loading...',
  showText = true,
  className = '',
  style = {},
  delay = 250,
}) => {
  const visible = useDelayedShow(delay);
  const arcSize = ({
    small:  18,
    medium: 26,
    large:  36,
    xlarge: 48,
  })[size] || 26;

  return (
    <div
      // `loader-container` retained for back-compat with existing tests
      className={`loader-container si-loader ${className}`}
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.875rem',
        padding: '1.5rem',
        opacity: visible ? 1 : 0,
        transition: 'opacity 220ms ease-out',
        ...style,
      }}
    >
      <Arc size={arcSize} />
      {showText && text && (
        <p
          style={{
            margin: 0,
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'var(--text-muted)',
            textAlign: 'center',
            letterSpacing: 0,
          }}
        >
          {text}
        </p>
      )}
      <GlobalKeyframes />
    </div>
  );
};

/**
 * InlineLoader — 14px arc replacement for button icon slots.
 * Compact, no chassis, sits flush with adjacent text.
 */
export const InlineLoader = ({ size = 'small', className = '', color, style = {} }) => {
  const sizeMap = { small: 14, medium: 18, large: 22 };
  const px = sizeMap[size] || sizeMap.small;
  return (
    <span
      // `inline-loader` retained for back-compat with existing tests
      className={`inline-loader si-inline-loader ${className}`}
      role="status"
      aria-live="polite"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '0.5rem',
        ...style,
      }}
    >
      <Arc size={px} color={color || 'currentColor'} />
      <GlobalKeyframes />
    </span>
  );
};

/**
 * FullPageLoader — used by 18 route-level early returns. Keeps the original
 * export signature so existing call sites (`<FullPageLoader text="..." />`)
 * continue to work. NO LONGER uses fixed positioning + backdrop blur — it
 * fills the page content area beside the sidebar instead, which matches the
 * flat-solid design rule and keeps the sidebar visible during route loads.
 */
export const FullPageLoader = ({ text = 'Loading...', delay = 250 }) => {
  return (
    <div
      className="si-fullpage-loader"
      style={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        width: '100%',
        padding: '4rem 1.5rem',
        background: 'transparent',
      }}
    >
      <Loader size="large" text={text} delay={delay} />
    </div>
  );
};

/**
 * CardLoader — kept exported for backwards compatibility. Currently unused
 * anywhere in the app; safe to delete in a future cleanup.
 */
export const CardLoader = ({ text = 'Loading...' }) => (
  <div
    style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-lg)',
      padding: '2rem',
      margin: '1rem 0',
    }}
  >
    <Loader text={text} />
  </div>
);

/**
 * TextLoader — drop-in for `<p>Loading…</p>` / `<div>Loading X</div>` patterns
 * scattered across HomePage, TenantDashboard, reports, etc.
 * Renders as a single text-flow row: tiny arc + muted label.
 */
export const TextLoader = ({ text = 'Loading...', size = 12, className = '', style = {} }) => (
  <span
    className={`si-text-loader ${className}`}
    role="status"
    aria-live="polite"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: 'var(--text-muted)',
      lineHeight: 1.2,
      ...style,
    }}
  >
    <Arc size={size} color="currentColor" />
    <span>{text}</span>
    <GlobalKeyframes />
  </span>
);

/**
 * BootScreen — replaces the bare `<div>Loading...</div>` in App.js's Auth0
 * boot. First frame users see; treated as a brand moment, not a loading state.
 * Centered logo with breathing pulse + 2px × 96px indeterminate slide bar
 * (matches the 2px-bar motif used in card status indicators / tab underlines).
 *
 * Tokens hardcoded so this works pre-theme; uses CSS vars where available.
 */
export const BootScreen = ({ logoSrc }) => {
  let isDarkTheme = false;
  try {
    isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
  } catch (_) { /* SSR guard */ }
  const resolvedLogo = logoSrc || (isDarkTheme ? '/l_white.png' : '/navy_logo.png');

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading application"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.75rem',
        background: `var(--bg-primary, ${BG_PRIMARY})`,
        zIndex: 9999,
      }}
    >
      <img
        src={resolvedLogo}
        alt=""
        style={{
          height: '56px',
          width: 'auto',
          maxWidth: '60vw',
          animation: 'si-boot-pulse 1600ms cubic-bezier(0.4, 0, 0.6, 1) infinite',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: '96px',
          height: '2px',
          background: `var(--border-subtle, ${BORDER_SUBTLE})`,
          borderRadius: '1px',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '24px',
            height: '100%',
            background: `var(--accent-blue, ${ACCENT_BLUE})`,
            borderRadius: '1px',
            animation: 'si-boot-slide 1400ms cubic-bezier(0.65, 0, 0.35, 1) infinite',
          }}
        />
      </div>
      <GlobalKeyframes />
    </div>
  );
};

/**
 * RouteProgress — top-of-viewport 2px progress bar that fires on every
 * react-router location change. Borrows the 2px-bar motif from card status
 * indicators / tab underlines. Mount once in App.js, near the root.
 *
 * NOT a true progress indicator (we have no real progress signal); animates
 * 0 → 80% on route change, holds, snaps to 100% + fades out after a short
 * timeout. Mimics the Vercel / Linear nav feel.
 */
export const RouteProgress = () => {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const firstRenderRef = useRef(true);
  const timersRef = useRef([]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    setActive(true);
    setProgress(8);
    const t1 = setTimeout(() => setProgress(82), 40);
    const t2 = setTimeout(() => setProgress(100), 520);
    const t3 = setTimeout(() => setActive(false), 760);
    const t4 = setTimeout(() => setProgress(0), 1020);
    timersRef.current = [t1, t2, t3, t4];

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [location.pathname]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        pointerEvents: 'none',
        zIndex: 10000,
        opacity: active ? 1 : 0,
        transition: 'opacity 220ms ease',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background: `var(--accent-blue, ${ACCENT_BLUE})`,
          WebkitMaskImage:
            'linear-gradient(to left, #000 0, #000 calc(100% - 18px), rgba(0,0,0,0.35) 100%)',
          maskImage:
            'linear-gradient(to left, #000 0, #000 calc(100% - 18px), rgba(0,0,0,0.35) 100%)',
          transition:
            progress === 100
              ? 'width 220ms ease-out'
              : 'width 420ms cubic-bezier(0.65, 0, 0.35, 1)',
        }}
      />
    </div>
  );
};

export default Loader;
